import * as v from "valibot";
import type {
  RefundOperation,
  RefundPaymentResult,
  StartRefundOperationRequest,
} from "../shared/types";
import { WorkerRequestError } from "./worker-error";

export interface WorkerRoutes {
  operations: string;
  operation: (operationId: string) => string;
  payment: (operationId: string) => string;
  openOperation: (accountId: string) => string;
}

export const defaultWorkerRoutes: WorkerRoutes = {
  operations: "/api/refund-operations",
  operation: (id) => `/api/refund-operations/${encodeURIComponent(id)}`,
  payment: (id) =>
    `/api/refund-operations/${encodeURIComponent(id)}/adyen-refund`,
  openOperation: (accountId) =>
    `/api/refund-operations/open?accountId=${encodeURIComponent(accountId)}`,
};

export interface DepositApiClientOptions {
  baseUrl?: string;
  installationId: string;
  installationSecret: string;
  timeoutMs?: number;
  fetch?: typeof fetch;
  routes?: WorkerRoutes;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function unwrap(value: unknown): unknown {
  if (!isRecord(value)) return value;
  if ("data" in value) return value.data;
  // Kept compatible with the Worker envelope used by the operation routes.
  if ("operation" in value) return value.operation;
  return value;
}

const integer = v.pipe(v.number(), v.integer());
const operationSchema = v.object({
  id: v.string(),
  installationId: v.string(),
  accountId: v.string(),
  idempotencyKey: v.string(),
  status: v.picklist([
    "draft",
    "items_booked",
    "awaiting_checkout",
    "refund_pending",
    "accepted",
    "adyen_succeeded",
    "pos_finalizing",
    "pos_finalization_failed",
    "completed",
    "blocked",
    "failed",
    "needs_reconciliation",
  ]),
  requestedItems: v.array(v.object({ itemId: v.string(), quantity: integer })),
  initialBalanceMinor: integer,
  initialItemQuantities: v.record(v.string(), integer),
  expectedBalanceDeltaMinor: integer,
  bookedBalanceMinor: v.optional(integer),
  refundAmountMinor: v.optional(integer),
  currency: v.string(),
  adyenPspReference: v.optional(v.string()),
  adyenTenderReference: v.optional(v.string()),
  terminalSettlementStatus: v.optional(
    v.picklist(["accepted", "approved", "declined"]),
  ),
  adyenTransactionType: v.optional(v.picklist(["payment", "refund"])),
  externalReference: v.optional(v.string()),
  failureReason: v.optional(v.string()),
  createdAt: v.string(),
  updatedAt: v.string(),
});

function assertOperation(value: unknown): RefundOperation {
  const result = v.safeParse(operationSchema, unwrap(value));
  if (!result.success)
    throw new Error("Worker returned an invalid refund operation payload");
  return result.output;
}

function assertOperations(value: unknown): RefundOperation[] {
  const data = unwrap(value);
  const operations =
    isRecord(data) && Array.isArray(data.operations)
      ? data.operations
      : Array.isArray(data)
        ? data
        : null;
  if (!operations) {
    throw new Error("Worker returned an invalid refund operation list");
  }
  return operations.map(assertOperation);
}

const paymentSchema = v.object({
  pspReference: v.optional(v.string()),
  tenderReference: v.optional(v.string()),
  settlementStatus: v.picklist(["accepted", "approved"]),
  amountMinor: integer,
});

function assertPayment(value: unknown): RefundPaymentResult {
  const result = v.safeParse(paymentSchema, unwrap(value));
  if (
    !result.success ||
    (!result.output.pspReference && !result.output.tenderReference)
  )
    throw new Error("Worker returned an invalid Adyen refund payload");
  return result.output;
}

export class DepositApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly routes: WorkerRoutes;

  constructor(private readonly options: DepositApiClientOptions) {
    this.baseUrl = options.baseUrl ?? "";
    if (options.fetch) {
      this.fetchImpl = options.fetch;
    } else {
      const globalFetch = globalThis.fetch;
      if (!globalFetch) throw new Error("fetch is unavailable");
      // Window.fetch is brand-checked by some browser/WebView runtimes. Bind
      // it before storing it on this client so it is not later invoked with
      // DepositApiClient as its receiver.
      this.fetchImpl = globalFetch.bind(globalThis);
    }
    // Terminal API requests can legitimately take up to roughly 150 seconds.
    this.timeoutMs = options.timeoutMs ?? 180_000;
    this.routes = options.routes ?? defaultWorkerRoutes;
  }

  async createOrResumeOperation(
    request: StartRefundOperationRequest,
  ): Promise<RefundOperation> {
    return assertOperation(
      await this.request(
        "POST",
        this.routes.operations,
        request,
        request.idempotencyKey,
      ),
    );
  }

  async getOperation(operationId: string): Promise<RefundOperation> {
    return assertOperation(
      await this.request("GET", this.routes.operation(operationId)),
    );
  }

  async listOperations(): Promise<RefundOperation[]> {
    return assertOperations(await this.request("GET", this.routes.operations));
  }

  async findOpenOperation(
    accountId: string,
  ): Promise<RefundOperation | undefined> {
    const result = unwrap(
      await this.request(
        "GET",
        this.routes.openOperation(accountId),
        undefined,
        undefined,
        [404],
      ),
    );
    if (result === undefined || result === null) return undefined;
    return assertOperation(result);
  }

  async updateOperation(
    operationId: string,
    patch: Partial<RefundOperation>,
  ): Promise<RefundOperation> {
    return assertOperation(
      await this.request("PATCH", this.routes.operation(operationId), patch),
    );
  }

  /** The worker must pass the operation idempotency key through to Adyen. */
  async requestAdyenRefund(
    operationId: string,
    amountMinor: number,
    idempotencyKey: string,
    transactionType: "payment" | "refund" = "refund",
  ): Promise<RefundPaymentResult> {
    return assertPayment(
      await this.request(
        "POST",
        this.routes.payment(operationId),
        { amountMinor, transactionType },
        idempotencyKey,
      ),
    );
  }

  private async request(
    method: string,
    path: string,
    body?: unknown,
    idempotencyKey?: string,
    allowStatus: number[] = [],
  ): Promise<unknown> {
    const controller = new AbortController();
    const timeout = setTimeout(
      () =>
        controller.abort(
          new Error(`Worker request timed out after ${this.timeoutMs}ms`),
        ),
      this.timeoutMs,
    );
    try {
      const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        signal: controller.signal,
        headers: {
          accept: "application/json",
          ...(body === undefined ? {} : { "content-type": "application/json" }),
          "x-installation-id": this.options.installationId,
          "x-installation-secret": this.options.installationSecret,
          authorization: `Bearer ${this.options.installationSecret}`,
          ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      if (allowStatus.includes(response.status)) return undefined;
      const contentType = response.headers.get("content-type") ?? "";
      const payload: unknown = contentType.includes("application/json")
        ? await response.json()
        : undefined;
      if (!response.ok) {
        const error = isRecord(payload) ? payload.error : undefined;
        const message =
          typeof error === "string"
            ? error
            : isRecord(error) && typeof error.message === "string"
              ? error.message
              : `Worker request failed (${response.status})`;
        throw new WorkerRequestError(message, {
          status: response.status,
          code:
            isRecord(error) && typeof error.code === "string"
              ? error.code
              : undefined,
          requestId:
            isRecord(error) && typeof error.requestId === "string"
              ? error.requestId
              : undefined,
        });
      }
      if (payload === undefined)
        throw new Error("Worker returned a non-JSON response");
      return payload;
    } finally {
      clearTimeout(timeout);
    }
  }
}
